import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import { getGallery } from '../../seo/galleryConfig';

const faqs = [
  {
    q: 'What causes most household blocked drains?',
    a: 'Kitchen drains are usually blocked by accumulated grease, food fat and starch (rice, pasta) cementing together. Bathroom drains are typically blocked by hair, soap scum, and toothpaste residue. Toilets become blocked by excessive paper, wet wipes (even "flushable" ones — they are not), or sanitary products. External drains are most often blocked by tree-root ingress, broken pipework, or seasonal leaf-fall.'
  },
  {
    q: 'Can I clear a blockage myself before calling you?',
    a: 'For minor sink or basin blockages, boiling water followed by a plunger sometimes works. Avoid chemical drain unblockers — they corrode older pipework and rarely solve persistent blockages. For toilets, anything more than a single quick attempt with a plunger is best left to a professional; pushing further can damage the pan seal or push the blockage to a worse location.'
  },
  {
    q: 'Do you use chemicals or mechanical clearing?',
    a: 'Almost always mechanical. We use professional drain rods, electric drain snakes for longer runs, and high-pressure water jetting for tougher blockages. CCTV inspection cameras are used for diagnosis on persistent or repeated blockages. Chemicals are a last resort and only used where safe and appropriate.'
  },
  {
    q: 'How quickly can you attend a blocked drain emergency?',
    a: 'For toilet and main waste blockages we treat it as urgent — typically attending within 2-4 hours during the day and under 90 minutes for genuine 24/7 emergencies (e.g. only toilet in the property, or sewage backflow).'
  },
  {
    q: 'Will you provide a written cause-of-blockage report?',
    a: 'Yes, especially for landlord and lettings situations. Where the blockage was caused by inappropriate items being flushed, photographic evidence helps with tenant cost recovery and insurance discussions.'
  },
];

const relatedServices = [
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'General plumbing repairs and upgrades.' },
  { slug: 'toilet-repairs', label: 'Toilet Repairs', blurb: 'When the blockage is just one symptom of a bigger issue.' },
  { slug: 'leak-detection-repairs', label: 'Leak Detection', blurb: 'A persistent blocked drain can mask a leak.' },
];

const BlockedDrainsPage = () => (
  <ServicePageLayout
    seoTitle="Blocked Drain & Drain Unblocking Services in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Professional blocked drain clearance and unblocking across Kent, London and Essex. Sink, toilet, bath, external drains. High-pressure jetting, CCTV inspection. Call +44 330 122 1094."
    canonicalPath="/services/blocked-drains"
    keywords="blocked drain, drain unblocking, drain cleaning, blocked toilet, blocked sink, drain jetting, blocked drain Kent, blocked drain London, blocked drain Essex"
    pageTitle="Blocked Drain Unblocking Across Kent, London & Essex"
    serviceName="Blocked Drains"
    serviceSlug="blocked-drains"
    heroSubheading="Cleared fast. Diagnosed properly. No quick fixes that fail next week."
    intro="A blocked drain is one of those problems that goes from annoying to disastrous in hours. Slow-running water becomes a sink full. A struggling toilet becomes one that overflows. H.A.R.P Solutions clears blocked drains across Kent, London and Essex using professional rodding, electric snakes, and high-pressure jetting — and we diagnose the root cause so the problem does not return next month."
    bullets={[
      'Sinks, basins, baths, showers, toilets, external drains',
      'Professional drain rods + electric snake equipment',
      'High-pressure water jetting for tough blockages',
      'CCTV camera inspection on persistent issues',
      'Same-day attendance for most domestic jobs',
      'Written reports for landlord cost-recovery',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
    galleryImages={getGallery('blocked-drains')}
  >
    <h2>Blockages We Clear Daily</h2>
    <ul>
      <li><strong>Kitchen sink blockages</strong> — grease, fat and food-debris build-up in the trap and waste pipe.</li>
      <li><strong>Bathroom basin and bath blockages</strong> — hair, soap and toothpaste residue cementing into the trap.</li>
      <li><strong>Shower drain blockages</strong> — hair tangling around shower waste fittings.</li>
      <li><strong>Toilet blockages</strong> — excess paper, wet wipes, sanitary products, foreign objects.</li>
      <li><strong>External gully blockages</strong> — leaf-fall, silt, debris washed in from gardens or driveways.</li>
      <li><strong>Main sewer blockages</strong> — tree-root ingress, collapsed pipe sections, fat accumulation.</li>
    </ul>

    <h2>Our Process</h2>
    <ol>
      <li><strong>Diagnose.</strong> Where is the blockage, what's likely causing it, and is it a one-off or symptom of a bigger issue?</li>
      <li><strong>Clear.</strong> We start with the least-invasive method (plunger or rods) and escalate as needed (electric snake, then high-pressure jet).</li>
      <li><strong>Investigate root cause.</strong> For repeated blockages we run a CCTV camera down the affected run to identify pipe damage, root ingress, or design flaws.</li>
      <li><strong>Recommend.</strong> If the problem will return without further work, we explain why and quote the long-term fix — but you decide.</li>
    </ol>

    <h2>When To Treat It As An Emergency</h2>
    <p>Call us straight away if:</p>
    <ul>
      <li>The only toilet in your property won't flush properly.</li>
      <li>Sewage is backing up into showers, baths or floor drains.</li>
      <li>You have foul odours combined with gurgling sounds — usually a partial main blockage.</li>
      <li>Your business cannot trade safely while drains are out of action.</li>
    </ul>

    <h2>Pricing &amp; Guarantees</h2>
    <p>
      We work on fixed-price quotes wherever possible. The price you are quoted is the price you pay — even if the job turns out tougher than
      first thought. Where the blockage points to deeper pipe damage that needs further work, we'll explain it clearly and quote that
      separately so you can decide.
    </p>

    <h2>Book Now</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> or use the form below. For 24/7 emergencies see our{' '}
      <a href="/services/emergency-plumber">emergency plumber page</a>.
    </p>
  </ServicePageLayout>
);

export default BlockedDrainsPage;
